import { useEffect, useState, useCallback } from "react";
import Layout from "@/components/Layout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useClientScope } from "@/hooks/useClientScope";
import { toast } from "sonner";
import { FileText, Loader2, PenLine } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { VerdictBadge, ComponentBars, scoreColor } from "@/components/BrandBits";

const REACTION_ORDER = ["like", "love", "haha", "wow", "sad", "angry"];

function ReportCard({ id }) {
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const reply = async () => {
    setBusy(true);
    try {
      const qs = user?.role === "super_admin" && r.client_id ? `?client_id=${r.client_id}` : "";
      await api.post(`/content/planner/reply${qs}`, { post_id: r.post_id, account_id: r.account_id });
      toast.success("Reply drafted. Nothing is posted until you approve it.", { action: { label: "Open", onClick: () => navigate("/content?tab=planner") } });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  useEffect(() => {
    setR(null);
    api.get(`/brand/posts/${id}`).then(({ data }) => setR(data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, [id]);
  if (!r) return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  const s = r.sentiment;
  return (
    <div className="space-y-6 mt-4" data-testid="post-report-card">
      <div className="flex items-center gap-3">
        <span className="font-heading text-5xl font-medium" style={{ color: scoreColor(r.score) }}>{r.score ?? "–"}</span>
        <VerdictBadge verdict={r.verdict} />
      </div>
      <p className="text-sm text-slate-600">
        {r.comment_count} comments, {Math.round((r.hostile_ratio || 0) * 100)}% hostile.
        {r.permalink && <> <a href={r.permalink} target="_blank" rel="noreferrer" className="underline">Open post</a></>}
      </p>
      {user?.role !== "client_viewer" && r.comment_count > 0 && (
        <button onClick={reply} disabled={busy} data-testid="reply-to-post"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-indigo-600 hover:bg-indigo-700 text-indigo-50 disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PenLine className="w-4 h-4" />}Reply to this post's issues
        </button>
      )}
      <section>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-3">Score components</h3>
        <ComponentBars components={r.components} />
      </section>
      <section>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Sentiment</h3>
        <p className="text-sm text-slate-600">
          <span className="font-mono">{s.positive}</span> positive, <span className="font-mono">{s.neutral}</span> neutral, <span className="font-mono">{s.negative}</span> negative
        </p>
      </section>
      <section>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Reactions</h3>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
          {REACTION_ORDER.filter((k) => r.reactions[k] != null).map((k) => (
            <span key={k}><span className="font-mono">{r.reactions[k]}</span> {k}</span>
          ))}
          {!Object.keys(r.reactions).length && <span className="text-slate-400">No reaction data</span>}
        </div>
      </section>
      <section>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Top themes</h3>
        {r.themes.length ? (
          <div className="flex flex-wrap gap-2">
            {r.themes.map((t) => (
              <span key={t.theme} className="px-2 py-1 rounded-md bg-slate-100 text-xs text-slate-700 capitalize">{t.theme} <span className="font-mono text-slate-500">{t.count}</span></span>
            ))}
          </div>
        ) : <p className="text-sm text-slate-400">None detected.</p>}
      </section>
      <section>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Top hostile commenters</h3>
        {r.hostile_commenters.length ? (
          <ul className="space-y-3" data-testid="hostile-commenters">
            {r.hostile_commenters.map((h) => (
              <li key={h.author_name + h.sample} className="text-sm">
                <div className="flex justify-between"><span className="font-medium text-slate-800">{h.author_name}</span>
                  <span className="font-mono text-slate-500">{h.count} hostile</span></div>
                <p className="text-slate-500 truncate">{h.sample}</p>
              </li>
            ))}
          </ul>
        ) : <p className="text-sm text-slate-400">No hostile commenters.</p>}
      </section>
    </div>
  );
}

export default function PostReports() {
  const { ready, scopeParam, ClientPicker } = useClientScope();
  const [rows, setRows] = useState(null);
  const [verdict, setVerdict] = useState("");
  const [sort, setSort] = useState("recent");
  const [open, setOpen] = useState(null);

  const load = useCallback(() => {
    if (!ready) return;
    const q = (scopeParam ? `&${scopeParam}` : "") + (verdict ? `&verdict=${verdict}` : "");
    api.get(`/brand/posts?sort=${sort}${q}`).then(({ data }) => setRows(data))
      .catch((e) => { setRows([]); toast.error(formatApiErrorDetail(e.response?.data?.detail)); });
  }, [ready, scopeParam, verdict, sort]);

  useEffect(() => { load(); }, [load]);

  return (
    <Layout>
      <div className="animate-fade-in-up space-y-6" data-testid="post-reports-page">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="text-sm text-slate-400 font-medium">How each post landed</p>
            <h1 className="font-heading text-3xl sm:text-4xl font-medium text-slate-900 tracking-tight">Post Reports</h1>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <ClientPicker />
            <select value={verdict} onChange={(e) => setVerdict(e.target.value)} data-testid="post-verdict-filter"
              className="px-3 py-1.5 rounded-lg border border-input bg-card text-sm text-slate-700">
              <option value="">All verdicts</option>
              <option value="healthy">Healthy</option>
              <option value="mixed">Mixed</option>
              <option value="damaged">Damaged</option>
            </select>
            <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="post-sort"
              className="px-3 py-1.5 rounded-lg border border-input bg-card text-sm text-slate-700">
              <option value="recent">Most recent</option>
              <option value="worst">Lowest score</option>
            </select>
          </div>
        </div>

        {rows === null && <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>}
        {rows && rows.length === 0 && (
          <div className="bg-card rounded-lg border border-slate-100 p-10 text-center text-slate-500">
            <FileText className="w-8 h-8 mx-auto mb-3 text-slate-300" />
            No posts to report on yet.
          </div>
        )}
        {rows && rows.length > 0 && (
          <div className="bg-card rounded-lg border border-slate-100 shadow-sm divide-y divide-slate-100" data-testid="post-list">
            {rows.map((p) => (
              <button key={p.id} onClick={() => setOpen(p.id)} data-testid={`post-row-${p.id}`}
                className="w-full text-left px-4 py-3 flex items-center gap-4 hover:bg-slate-50/60 transition-colors">
                <span className="font-heading text-2xl w-12 text-center" style={{ color: scoreColor(p.score) }}>{p.score ?? "–"}</span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-800 truncate">{p.caption || "(no caption)"}</p>
                  <p className="text-xs text-slate-400">{p.account_name} · {p.comment_count} comments · {Math.round((p.hostile_ratio || 0) * 100)}% hostile</p>
                </div>
                <VerdictBadge verdict={p.verdict} />
              </button>
            ))}
          </div>
        )}

        <Sheet open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
          <SheetContent className="w-full sm:max-w-md overflow-y-auto">
            <SheetHeader>
              <SheetTitle>Post report card</SheetTitle>
              <SheetDescription>Sentiment, themes and hostile commenters for this post.</SheetDescription>
            </SheetHeader>
            {open && <ReportCard id={open} />}
          </SheetContent>
        </Sheet>
      </div>
    </Layout>
  );
}
